import React, { useState, useEffect } from 'react'; 
import { useNavigate } from 'react-router-dom'; 
import { useQuery, useMutation } from '@tanstack/react-query';
import axiosClient from '../../api/axiosClient';
import { 
  Mail, CreditCard, Save, Server, Lock, Eye, EyeOff, DownloadCloud,
  Send, AlertTriangle, ShieldCheck, FileText, LayoutTemplate 
} from 'lucide-react';

import { usePermission } from '../../hooks/usePermission';

// --- Reusable Input Component ---
const SecureInput = ({ label, value, onChange, placeholder }) => {
  const [show, setShow] = useState(false);
  return (
    <div>
      <label className="block text-xs font-bold text-slate-500 uppercase mb-1 tracking-wide">{label}</label>
      <div className="relative">
        <input 
          type={show ? "text" : "password"}
          className="w-full pl-4 pr-10 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm font-mono transition-all hover:border-slate-300"
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          autoComplete="new-password"
        />
        <button 
          type="button"
          onClick={() => setShow(!show)}
          className="absolute right-3 top-2.5 text-slate-400 hover:text-slate-600 transition-colors"
        >
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
};

const SettingCard = ({ icon: Icon, title, subtitle, color, children }) => (
  <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden h-fit transition-shadow hover:shadow-md">
    <div className="p-6 border-b border-slate-100 bg-slate-50/50 flex justify-between items-center">
      <div className="flex items-center">
        <div className={`p-2 bg-${color}-100 text-${color}-600 rounded-lg mr-3 shadow-sm`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <h3 className="font-bold text-slate-800">{title}</h3>
          <p className="text-xs text-slate-500 font-medium">{subtitle}</p>
        </div>
      </div>
    </div>
    <div className="p-6 space-y-5">
      {children}
    </div>
  </div>
);

const GlobalSettingsPage = () => {
  const navigate = useNavigate(); 
  const { can } = usePermission();
  
  // --- STATE ---
  const [emailConfig, setEmailConfig] = useState({
    server: '', port: 587, username: '', password: '', 
    from_email: '', use_tls: true, use_ssl: false
  });
  const [testEmail, setTestEmail] = useState('');
  const [razorpayConfig, setRazorpayConfig] = useState({ key_id: '', key_secret: '' });
  const [invoiceConfig, setInvoiceConfig] = useState({
    invoice_prefix: 'AIRS-', invoice_start_number: 1000, order_prefix: 'ORD-',
    gst_percent: 18.0, tax_name: 'GST', company_address: '', gstin_number: ''
  });

  // --- STATE: Agent Downloads ---
  const [downloadLinks, setDownloadLinks] = useState({
    linux: '', windows: '', macos: '', android: '', ios: ''
  });

  // --- QUERY: Fetch Links ---
  useQuery({
    queryKey: ['agentLinks'],
    queryFn: async () => {
      const res = await axiosClient.get('/installer/links'); // Hits api_installer.py
      if (res) setDownloadLinks(res); // Auto-fill inputs
      return res;
    }
  });

  // --- MUTATION: Save Links ---
  const saveLinksMutation = useMutation({
    mutationFn: (data) => axiosClient.post('/installer/links', data),
    onSuccess: () => alert("Download Links Updated Successfully! 🚀")
  });
  // 1. SMTP Config (Corrected & Clean)
  const { data: smtpData } = useQuery({
    queryKey: ['smtpConfig'],
    queryFn: async () => {
      const res = await axiosClient.get('/admin/settings/smtp');
      return res.data || res; 
    }
  });

  // ✅ Force Update State when Data Arrives
  useEffect(() => {
    if (smtpData) {
      setEmailConfig({
        server: smtpData.mail_server || '',
        port: smtpData.mail_port || 587,
        username: smtpData.mail_username || '',
        password: smtpData.mail_password ? '••••••••' : '',
        from_email: smtpData.mail_from || '',
        use_tls: !!smtpData.mail_starttls,
        use_ssl: !!smtpData.mail_ssl_tls
      });
    }
  }, [smtpData]);

  // 2. Payment Config
  useQuery({
    queryKey: ['paymentConfig'],
    queryFn: async () => {
      const res = await axiosClient.get('/admin/settings/payment-gateway');
      if (res && res.key_id) setRazorpayConfig({ key_id: res.key_id, key_secret: res.key_secret ? '••••••••' : '' });
      return res;
    }
  });

  // 3. Invoice Config
  useQuery({
    queryKey: ['invoiceConfig'],
    queryFn: async () => {
      const res = await axiosClient.get('/admin/settings/invoice');
      if (res && res.invoice_prefix) setInvoiceConfig(res);
      return res;
    }
  });

  // --- MUTATIONS ---
  const saveSmtpMutation = useMutation({
    mutationFn: (data) => {
      const payload = { ...data };
      if (payload.password === '••••••••') delete payload.password; 
      return axiosClient.post('/admin/settings/smtp', payload);
    },
    onSuccess: () => alert("Email Settings Saved Successfully!")
  });

  const testSmtpMutation = useMutation({
    mutationFn: (email) => axiosClient.post('/admin/settings/smtp/test', { target_email: email }),
    onSuccess: () => alert("Test Email Sent! Check your inbox."),
    onError: (err) => alert(`Failed: ${err.message}`)
  });

  const savePaymentMutation = useMutation({
    mutationFn: (data) => {
      const payload = { provider: 'razorpay', ...data };
      if (payload.key_secret === '••••••••') delete payload.key_secret;
      return axiosClient.post('/admin/settings/payment-gateway', payload);
    },
    onSuccess: () => alert("Payment Keys Updated Securely!")
  });

  const saveInvoiceMutation = useMutation({
    mutationFn: (data) => axiosClient.post('/admin/settings/invoice', data),
    onSuccess: () => alert("Finance Settings Updated!")
  });

  return (
    <div className="space-y-8 animate-fade-in pb-20 max-w-7xl mx-auto">
      
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-800 flex items-center">
          <ShieldCheck className="w-8 h-8 text-blue-600 mr-3" />
          System Configuration
        </h1>
        <p className="text-slate-500 mt-1 ml-11">Manage core integrations, finance rules, and communication channels.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

        {/* 4. AGENT BINARIES (Download Links) */}
        <div className="lg:col-span-2">
            <SettingCard icon={DownloadCloud} title="Agent Binaries" subtitle="Direct Download Links for User Portal" color="purple">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    
                    {/* Linux Script */}
                    <div className="col-span-2">
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Linux Installer Script (Curl URL)</label>
                        <div className="flex gap-2">
                            <div className="p-2.5 bg-slate-100 border border-slate-200 rounded-lg font-mono text-slate-500 text-sm">curl -sL</div>
                            <input 
                                className="flex-1 p-2.5 border border-slate-200 rounded-lg text-sm font-mono" 
                                placeholder="https://airssecurity.com/install.sh"
                                value={downloadLinks.linux}
                                onChange={e => setDownloadLinks({...downloadLinks, linux: e.target.value})}
                            />
                        </div>
                        <p className="text-[10px] text-slate-400 mt-1">Users will run: curl -sL [URL] | sudo bash -s -- --license KEY</p>
                    </div>

                    {/* Other OS Links */}
                    {['windows', 'macos', 'android', 'ios'].map((os) => (
                        <div key={os}>
                            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">{os} Installer URL</label>
                            <input 
                                className="w-full p-2.5 border border-slate-200 rounded-lg text-sm" 
                                placeholder={`https://.../agent.${os === 'windows' ? 'exe' : os === 'macos' ? 'dmg' : 'apk'}`}
                                value={downloadLinks[os]}
                                onChange={e => setDownloadLinks({...downloadLinks, [os]: e.target.value})}
                            />
                        </div>
                    ))}

                    <div className="col-span-2">
                        <button 
                            onClick={() => saveLinksMutation.mutate(downloadLinks)}
                            disabled={saveLinksMutation.isLoading}
                            className="w-full bg-purple-600 text-white py-3 rounded-xl font-bold hover:bg-purple-700 shadow-lg shadow-purple-500/20 flex justify-center items-center transition-transform active:scale-95"
                        >
                            {saveLinksMutation.isLoading ? 'Saving...' : <><Save className="w-4 h-4 mr-2"/> Update Download Links</>}
                        </button>
                    </div>
                </div>
            </SettingCard>
        </div>
    
        {/* 1. EMAIL CONFIGURATION */}
        <SettingCard icon={Mail} title="SMTP Email Server" subtitle="For OTPs, Invoices & Alerts" color="blue">
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2">
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">SMTP Host</label>
                <div className="relative">
                  <Server className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
                  <input 
                    className="w-full pl-9 p-2.5 border border-slate-200 rounded-lg text-sm transition-all hover:border-slate-300 focus:ring-2 focus:ring-blue-500 outline-none" 
                    placeholder="smtp.gmail.com"
                    value={emailConfig.server}
                    onChange={e => setEmailConfig({...emailConfig, server: e.target.value})}
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Port</label>
                <input 
                  type="number" 
                  className="w-full p-2.5 border border-slate-200 rounded-lg text-sm text-center font-mono" 
                  placeholder="587"
                  value={emailConfig.port}
                  onChange={e => setEmailConfig({...emailConfig, port: parseInt(e.target.value)})}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Username</label>
                <input 
                  className="w-full p-2.5 border border-slate-200 rounded-lg text-sm font-medium" 
                  placeholder="apikey"
                  value={emailConfig.username}
                  onChange={e => setEmailConfig({...emailConfig, username: e.target.value})}
                />
              </div>
              <SecureInput 
                label="Password / API Key" 
                placeholder="••••••••••••"
                value={emailConfig.password}
                onChange={e => setEmailConfig({...emailConfig, password: e.target.value})}
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Sender Email (From)</label>
              <input 
                className="w-full p-2.5 border border-slate-200 rounded-lg text-sm" 
                placeholder="notifications@airs-security.com"
                value={emailConfig.from_email}
                onChange={e => setEmailConfig({...emailConfig, from_email: e.target.value})}
              />
            </div>

            <div className="flex space-x-6 pt-2 pb-2">
              <label className="flex items-center space-x-2 cursor-pointer group">
                <input 
                  type="checkbox" 
                  className="rounded text-blue-600 focus:ring-blue-500 w-4 h-4 cursor-pointer"
                  checked={emailConfig.use_tls}
                  onChange={e => setEmailConfig({...emailConfig, use_tls: e.target.checked})}
                />
                <span className="text-sm text-slate-700 font-medium group-hover:text-blue-600 transition-colors">Use TLS</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer group">
                <input 
                  type="checkbox" 
                  className="rounded text-blue-600 focus:ring-blue-500 w-4 h-4 cursor-pointer"
                  checked={emailConfig.use_ssl}
                  onChange={e => setEmailConfig({...emailConfig, use_ssl: e.target.checked})}
                />
                <span className="text-sm text-slate-700 font-medium group-hover:text-blue-600 transition-colors">Use SSL</span>
              </label>
            </div>

            <div className="flex gap-2 pt-2 border-t border-slate-100 mt-2">
                <input 
                  placeholder="Enter email to receive test..." 
                  className="flex-1 p-2.5 border border-slate-200 rounded-lg text-sm"
                  value={testEmail}
                  onChange={e => setTestEmail(e.target.value)}
                />
                <button 
                  onClick={() => testSmtpMutation.mutate(testEmail)}
                  disabled={!testEmail || testSmtpMutation.isLoading}
                  className="bg-slate-800 text-white px-4 py-2 rounded-lg text-xs font-bold flex items-center hover:bg-slate-700 disabled:opacity-50 transition-colors"
                >
                  {testSmtpMutation.isLoading ? '...' : <Send className="w-4 h-4" />}
                </button>
            </div>

            {can('settings:manage') && (
                <button 
                onClick={() => saveSmtpMutation.mutate(emailConfig)}
                disabled={saveSmtpMutation.isLoading}
                className="w-full bg-blue-600 text-white py-3 rounded-xl font-bold hover:bg-blue-700 shadow-lg shadow-blue-500/20 flex justify-center items-center mt-2 transition-transform active:scale-95"
                >
                {saveSmtpMutation.isLoading ? 'Saving...' : <><Save className="w-4 h-4 mr-2"/> Save Configuration</>}
                </button>
            )}
        </SettingCard>

        {/* 2. PAYMENT & EXTRAS */}
        <div className="space-y-6">
            <SettingCard icon={CreditCard} title="Payment Gateway" subtitle="Razorpay Integration" color="indigo">
                <div className="p-4 bg-amber-50 border border-amber-100 rounded-xl flex items-start space-x-3 mb-4">
                    <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" />
                    <p className="text-xs text-amber-700 leading-relaxed font-medium">
                        Enter <b>Test Mode</b> keys for development or <b>Live Mode</b> keys for production. Keys are encrypted.
                    </p>
                </div>

                <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Key ID</label>
                    <div className="relative">
                        <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
                        <input 
                        className="w-full pl-9 p-2.5 border border-slate-200 rounded-lg text-sm font-mono" 
                        placeholder="rzp_test_..."
                        value={razorpayConfig.key_id}
                        onChange={e => setRazorpayConfig({...razorpayConfig, key_id: e.target.value})}
                        />
                    </div>
                </div>

                <SecureInput 
                    label="Key Secret" 
                    placeholder={razorpayConfig.key_id ? "•••••••• (Stored Securely)" : "Enter new secret..."}
                    value={razorpayConfig.key_secret}
                    onChange={e => setRazorpayConfig({...razorpayConfig, key_secret: e.target.value})}
                />

                <button 
                    onClick={() => savePaymentMutation.mutate(razorpayConfig)}
                    disabled={savePaymentMutation.isLoading}
                    className="w-full bg-indigo-600 text-white py-3 rounded-xl font-bold hover:bg-indigo-700 shadow-lg shadow-indigo-500/20 flex justify-center items-center mt-4 transition-transform active:scale-95"
                >
                    {savePaymentMutation.isLoading ? 'Updating...' : <><Save className="w-4 h-4 mr-2"/> Update Keys Securely</>}
                </button>
            </SettingCard>

            {/* Email Template Button */}
            <button 
                onClick={() => navigate('/settings/email-templates')}
                className="w-full p-6 bg-white border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-all text-left group flex items-center justify-between"
            >
                <div className="flex items-center">
                    <div className="w-12 h-12 bg-purple-100 text-purple-600 rounded-xl flex items-center justify-center mr-4 group-hover:scale-110 transition-transform">
                        <LayoutTemplate className="w-6 h-6" />
                    </div>
                    <div>
                        <h3 className="font-bold text-slate-800 text-lg">Email Templates</h3>
                        <p className="text-xs text-slate-500 mt-1">Customize HTML emails for Invoices & OTPs.</p>
                    </div>
                </div>
                <div className="text-slate-300 group-hover:text-purple-600 transition-colors">
                    ➔
                </div>
            </button> 
        </div>

        {/* 3. INVOICE & TAX SETTINGS (Full Width) */}
        <div className="lg:col-span-2">
            <SettingCard icon={FileText} title="Finance & Legal" subtitle="Invoice Formatting and Tax Rules" color="emerald">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    
                    <div className="space-y-4">
                        <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest border-b pb-2">Numbering</h4>
                        <div>
                            <label className="block text-xs font-bold text-slate-600 mb-1">Invoice Prefix</label>
                            <input className="w-full p-2 border rounded font-mono text-sm" placeholder="AIRS-" value={invoiceConfig.invoice_prefix} onChange={e => setInvoiceConfig({...invoiceConfig, invoice_prefix: e.target.value})} />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-slate-600 mb-1">Next Invoice #</label>
                            <input type="number" className="w-full p-2 border rounded font-mono text-sm" value={invoiceConfig.invoice_start_number} onChange={e => setInvoiceConfig({...invoiceConfig, invoice_start_number: e.target.value})} />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-slate-600 mb-1">Order Prefix</label>
                            <input className="w-full p-2 border rounded font-mono text-sm" placeholder="ORD-" value={invoiceConfig.order_prefix} onChange={e => setInvoiceConfig({...invoiceConfig, order_prefix: e.target.value})} />
                        </div>
                    </div>

                    <div className="space-y-4">
                        <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest border-b pb-2">Taxation</h4>
                        <div>
                            <label className="block text-xs font-bold text-slate-600 mb-1">Tax Name</label>
                            <input className="w-full p-2 border rounded text-sm" placeholder="GST / VAT" value={invoiceConfig.tax_name} onChange={e => setInvoiceConfig({...invoiceConfig, tax_name: e.target.value})} />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-slate-600 mb-1">Tax Rate (%)</label>
                            <input type="number" className="w-full p-2 border rounded text-sm" placeholder="18.00" value={invoiceConfig.gst_percent} onChange={e => setInvoiceConfig({...invoiceConfig, gst_percent: e.target.value})} />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-slate-600 mb-1">Tax ID (GSTIN)</label>
                            <input className="w-full p-2 border rounded font-mono text-sm uppercase" placeholder="22AAAAA0000A1Z5" value={invoiceConfig.gstin_number} onChange={e => setInvoiceConfig({...invoiceConfig, gstin_number: e.target.value})} />
                        </div>
                    </div>

                    <div className="space-y-4 flex flex-col">
                        <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest border-b pb-2">Company Details</h4>
                        <div className="flex-1">
                            <label className="block text-xs font-bold text-slate-600 mb-1">Registered Address (For Invoice)</label>
                            <textarea 
                                className="w-full p-3 border rounded-lg h-32 text-sm leading-relaxed resize-none focus:ring-2 focus:ring-emerald-500 outline-none" 
                                placeholder="Appence Software Solution..." 
                                value={invoiceConfig.company_address} 
                                onChange={e => setInvoiceConfig({...invoiceConfig, company_address: e.target.value})} 
                            />
                        </div>
                        <button onClick={() => saveInvoiceMutation.mutate(invoiceConfig)} className="w-full bg-emerald-600 text-white py-2.5 rounded-lg font-bold text-sm hover:bg-emerald-700 shadow-lg shadow-emerald-500/20 transition-transform active:scale-95">
                            SAVE FINANCE SETTINGS
                        </button>
                    </div>
                </div>
            </SettingCard>
        </div>

      </div>
    </div>
  );
};

export default GlobalSettingsPage;